import React, { useState } from 'react';
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { Fingerprint, Upload, Download, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function FingerExtractor() {
  const [file, setFile] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [isoImageUrl, setIsoImageUrl] = useState(null);
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [timing, setTiming] = useState({ convertInput: 0, convertJpeg: 0, extract: 0, convertIso: 0 });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [jp2isoImageUrl, setjp2IsoImageUrl] = useState(null);

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const convertFingerImageToIso = async (inputFile, subType) => {
    const formData = new FormData();
    formData.append('file', inputFile);

    const candidateUrls = [
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=${subType}`,
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN&purpose=AUTH`,
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN`,
    ];

    let lastError = null;
    for (const url of candidateUrls) {
      try {
        const response = await axios.post(url, formData, {
          responseType: 'arraybuffer',
        });
        return response;
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError;
  };

  const handleFileChange = async (e) => {
    const subType = selectedOption.split('|')[0]; 
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    setInJpegImageUrl(null);
    let startConvertInput = 0;
    let convertInputDuration = 0;

    try {
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
        setIsoImageUrl(null);
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        startConvertInput = performance.now();

        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=FINGER`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        const jpegUrl = URL.createObjectURL(jpegBlob);
        setInJpegImageUrl(jpegUrl);
        setIsoImageUrl(null);
      } 
      else if (fileExtension === 'jp2') {
        const isoResponse = await convertFingerImageToIso(selectedFile, subType);
      
        const isoBlob = new Blob([isoResponse.data], { type: 'application/octet-stream' });
        const isoUrl = URL.createObjectURL(isoBlob);
        setjp2IsoImageUrl(isoUrl); 
    
        const isoFormData = new FormData();
        isoFormData.append('file', isoBlob, 'converted-jp2.iso'); 
    
        const jpegResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=FINGER`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },  
          responseType: 'arraybuffer',  
        });
      
        const jpegBlob = new Blob([jpegResponse.data], { type: 'image/jpeg' });
        const jpegUrl = URL.createObjectURL(jpegBlob);
        setInJpegImageUrl(jpegUrl);  
      } else {
        setError('Unsupported file type.');
        setFile(null);
        return;
      }

      setTiming((prevTiming) => ({
        ...prevTiming,
        convertInput: convertInputDuration,
      }));

      setIsoImageUrl(null);
      setShowAdvanced(false);
      setError(null);
      setSubmitted(false);
    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsProcessing(true);

    setIsoImageUrl(null);
    setShowAdvanced(false);
    setError(null);

    const fileExtension = file.name.split('.').pop().toLowerCase();
    let facebdbPlaceholder = [];
    let startConvertInput = 0;
    let convertInputDuration = 0;
    const subType = selectedOption.split('|')[0]; 

    try {
      if (fileExtension === 'jpeg' || fileExtension === 'jpg'|| fileExtension === 'bmp'|| fileExtension === 'png') {
        const isoResponse = await convertFingerImageToIso(file, subType);
        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        facebdbPlaceholder = toSignedByteJson(isoResponse.data);
      } 
      else if (fileExtension === 'jp2') {
        const isoBlob1 = await fetch(jp2isoImageUrl).then(res => res.arrayBuffer());
        facebdbPlaceholder = toSignedByteJson(isoBlob1);
      }
      else if (fileExtension === 'iso') {
        const isoArrayBuffer = await file.arrayBuffer();
        facebdbPlaceholder = toSignedByteJson(isoArrayBuffer);
      }

      const [position, finger] = selectedOption.split('|')[1].split(' '); 
      
      const templateResponse = await fetch('/single_finger_extraction_templagte_inner_request.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      const templateText = await templateResponse.text();
      const updatedTemplate = templateText
        .replace('<Left_IndexFinger_BDB>', facebdbPlaceholder)
        .replace('<Left>', position) 
        .replace('<IndexFinger>', finger);    

      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedExtract = btoa(updatedTemplate);
      const finalRequestBodyExtract = versionRequestTemplateText.replace('encoded_request', base64EncodedExtract);

      const startExtract = performance.now();
      const isRawInput = fileExtension !== 'iso';
      const extractUrl = biosdkOperationUrl('extract-template', isRawInput);
      logBiosdkUiRoute('FingerExtractor', isRawInput ? 'rawRoute' : 'isoRoute', extractUrl, {
        operation: 'extract-template',
      });
      const extractResponse = await axios.post(extractUrl, finalRequestBodyExtract, {
        headers: { 'Content-Type': 'application/json' },
      });
      const endExtract = performance.now();
      const extractDuration = endExtract - startExtract;

      const extractResult = extractResponse.data?.response?.response?.segments?.[0]?.bdb;
      if (!extractResult) throw new Error('Failed to extract the BDB from the response');

      const isoBlob = new Blob([new Uint8Array(extractResult)], { type: 'application/octet-stream' });
      const isoUrl = URL.createObjectURL(isoBlob);
      setIsoImageUrl(isoUrl);

      setTiming({
        convertInput: timing.convertInput,
        convertJpeg: convertInputDuration,
        extract: extractDuration,
        convertIso: 0,
      });
      setShowAdvanced(true);
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during the extraction process';
      setError(errorMessage);
    } finally {
      setIsProcessing(false); 
    }
  };

  const handleDownload = () => {
    if (isoImageUrl) {
      const link = document.createElement('a');
      link.href = isoImageUrl;
      link.download = 'extracted-image.iso';
      link.click();
    }
  };

  const handleReset = () => {
    setFile(null);
    setSelectedOption('');
    setIsoImageUrl(null);
    setInJpegImageUrl(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
  };

  const getFingerLabel = () => {
    if (!selectedOption) return 'Finger';
    return selectedOption.split('|')[1]?.replace(/([A-Z])/g, ' $1').trim() || 'Finger';
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card finger">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Fingerprint className="h-8 w-8 text-finger" />
              <CardTitle className="text-3xl font-bold text-gray-800">Finger Extractor</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Extract biometric templates from fingerprint images using ISO/IEC 19794-4 standard
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="fingerType" className="text-gray-700 font-medium">
                    Finger Type
                  </Label>
                  <Select
                    id="fingerType"
                    value={selectedOption}
                    onChange={(e) => setSelectedOption(e.target.value)}
                    className="w-full"
                  >
                    <option value="" disabled>Select finger type</option>
                    <option value="LEFT_INDEX_FINGER|Left IndexFinger">Left Index Finger</option>
                    <option value="LEFT_MIDDLE_FINGER|Left MiddleFinger">Left Middle Finger</option>
                    <option value="LEFT_RING_FINGER|Left RingFinger">Left Ring Finger</option>
                    <option value="LEFT_LITTLE_FINGER|Left LittleFinger">Left Little Finger</option>
                    <option value="LEFT_THUMB|Left Thumb">Left Thumb</option>
                    <option value="RIGHT_INDEX_FINGER|Right IndexFinger">Right Index Finger</option>
                    <option value="RIGHT_MIDDLE_FINGER|Right MiddleFinger">Right Middle Finger</option>
                    <option value="RIGHT_RING_FINGER|Right RingFinger">Right Ring Finger</option>
                    <option value="RIGHT_LITTLE_FINGER|Right LittleFinger">Right Little Finger</option>
                    <option value="RIGHT_THUMB|Right Thumb">Right Thumb</option>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    Upload Fingerprint Image
                  </Label>
                  <div className="upload-zone">
                    <input
                      type="file"
                      id="file"
                      accept=".jpeg,.jpg,.iso,.png,.jp2,.bmp"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, BMP, JP2, or ISO</p>
                    </label>
                  </div>
                </div>

                {file && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded fingerprint" className="preview-image" />
                      <Badge variant="finger" className="absolute top-2 right-2">{getFingerLabel()}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{file.name}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmit}
                    disabled={!file || !selectedOption || isProcessing}
                    variant="finger"
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Fingerprint className="mr-2 h-4 w-4" />
                        Extract Template
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>
                    Reset
                  </Button>
                </div>
              </div>

              {/* Output Section */}
              <div className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {isoImageUrl && !error && (
                  <>
                    <Alert variant="success">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertTitle>Extraction Complete</AlertTitle>
                      <AlertDescription>Fingerprint template extracted successfully</AlertDescription>
                    </Alert>

                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Extraction Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Finger Type:</span>
                          <Badge variant="finger">{getFingerLabel()}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Template Format:</span>
                          <span className="font-medium text-xs">ISO/IEC 19794-4</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className="font-medium text-green-600">Success</span>
                        </div>
                      </CardContent>
                    </Card>

                    {showAdvanced && (
                      <Card className="timing-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Timing Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Display Conversion:</span>
                            <span className="font-medium">{timing.convertInput.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">ISO Conversion:</span>
                            <span className="font-medium">{timing.convertJpeg.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Extraction Time:</span>
                            <span className="font-medium text-primary">{timing.extract.toFixed(2)} ms</span>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Button onClick={handleDownload} variant="outline" className="w-full">
                      <Download className="mr-2 h-4 w-4" />
                      Download ISO Template
                    </Button>
                  </>
                )}

                {!isoImageUrl && !error && submitted && (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}

                {!isoImageUrl && !error && !submitted && (
                  <div className="empty-state">
                    <Fingerprint className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload a fingerprint image and click Extract</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FingerExtractor;
